'use client'
import React, { useContext, useState, useEffect } from 'react';
import { createBearerJsonClient } from '../../utils/apiFetchPolicy';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from "react-router-dom";
import '../../styles/Home.css';
import '../../styles/Login.css';
import { AccountDataContext } from '../../context/AccountContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { trackAuth } from '../../utils/analytics';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../config/env';
import { getAuthHttpErrorMessage } from '../../utils/authHttpErrorMessage';
import { resolveAuthReturnPath } from '../../utils/s3MediaAuth';
import PasswordField from '../../components/auth/PasswordField';
import PhoneAuthPanel from '../../components/auth/PhoneAuthPanel';
import SocialLoginButtons from '../../components/auth/SocialLoginButtons';
import { startSocialLogin, fetchSocialProviders, fetchWechatQrUrl } from '../../utils/socialAuthApi';

const api = createBearerJsonClient(null, { baseURL: API_URL });

export const useAccountData = () => {
    const context = useContext(AccountDataContext);
    if (!context) {
        console.error('useAccountData must be used within an AccountDataProvider');
        // Return a default structure to prevent crashes during development
        return {
            setAuth: () => {},
            setToken: () => {},
            auth: null,
            token: null,
            email: null,
            password: null,
            addresses: [],
            setAddresses: () => {},
            paymentMethods: [],
            setPaymentMethods: () => {},
            accountDetails: null,
            setAccountDetails: () => {},
            fetchAccountData: () => {},
            isLoading: false,
        };
    }
    return context;
};

function Login() {
    const [email1, setEmail1] = useState('');
    const [password1, setPassword1] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [authHint, setAuthHint] = useState(null);
    const [resending, setResending] = useState(false);
    const [tab, setTab] = useState('email');
    const [providers, setProviders] = useState(null);
    const [wechatQr, setWechatQr] = useState(null);
    const { fetchAccountData, setToken, setAuth } = useAccountData();
    const email = email1;
    const password = password1;
    const { trackClick } = useAnalytics();
    const { t } = useTranslation();
    
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check for pending reservation/commission/service request and show message
    useEffect(() => {
        const pendingReservation = localStorage.getItem('pendingTutoringReservation');
        const pendingServiceReservation = localStorage.getItem('pendingServiceReservation');
        const pendingCommission = localStorage.getItem('pendingCommissionRequest');
        const pendingServiceRequest = localStorage.getItem('pendingServiceRequest');
        if (pendingReservation || location.state?.fromReservation) {
            const message = location.state?.message || 'Please login to link your reservation to your account';
            toast(message, { duration: 5000 });
        } else if (pendingServiceReservation || location.state?.fromServiceReservation) {
            const message = location.state?.message || 'Please login to link your service reservation to your account';
            toast(message, { duration: 5000 });
        } else if (pendingCommission || location.state?.fromCommission) {
            const message = location.state?.message || 'Please login to link your commission request to your account';
            toast(message, { duration: 5000 });
        } else if (pendingServiceRequest || location.state?.fromServiceRequest) {
            const message = location.state?.message || 'Please login to link your service request to your account';
            toast(message, { duration: 5000 });
        } else if (location.state?.fromMenu) {
            const message =
                location.state?.message || 'Please sign in to view hospitality menu item details';
            toast(message, { duration: 5000 });
        } else if (location.state?.message) {
            toast(location.state.message, { duration: 5000 });
        }
    }, [location.state]);

    // Discover which social providers are enabled so we only render their tabs.
    useEffect(() => {
        let cancelled = false;
        fetchSocialProviders()
            .then((res) => !cancelled && setProviders(res))
            .catch(() => !cancelled && setProviders({ google: { enabled: false }, wechat: { enabled: false } }));
        return () => {
            cancelled = true;
        };
    }, []);

    const nextPath = resolveAuthReturnPath(location) || '/account';

    // Prefill the email when we were redirected from a "this email already has
    // an account" registration path (e.g. /login?email=...).
    useEffect(() => {
        const emailParam = new URLSearchParams(window.location.search).get('email');
        if (emailParam) {
            setEmail1(emailParam);
            setTab('email');
        }
    }, []);

    const handleResendConfirmation = async () => {
        try {
            setResending(true);
            await api.post('/auth/resend-confirmation', { email });
            toast.success(t('auth.confirmEmailResent', 'Confirmation email resent — please check your inbox.'));
        } catch (resendErr) {
            toast.error(getAuthHttpErrorMessage(resendErr, t));
        } finally {
            setResending(false);
        }
    };

    const handleWechat = async () => {
        if (providers?.wechat?.qr) {
            const url = await fetchWechatQrUrl({ next: nextPath });
            if (url) {
                setWechatQr(url);
                return;
            }
        }
        startSocialLogin('wechat', { next: nextPath });
    };

    const handleRegister = () => {
        trackClick('New User Button - Login Page');
        navigate({ pathname: '/register', search: location.search }, { state: location.state });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        trackClick('Login Submit Button');
        setSubmitting(true);
        try {
            const response = await api.post('/auth/login', {email, password});    
            if (response.status === 200) {
                const user = response.data.user;
                if (!user || !user._id) {
                    // API returned 200 but user object is missing or malformed
                    throw new Error(
                        user
                            ? 'Login succeeded but user object is missing _id field'
                            : 'Login succeeded but no user data returned'
                    );
                }
                toast.success('Login success');
                trackAuth('login');
                trackClick('Login Submit - Success');
                setAuth(user);
                setToken(response.data.token);
                
                // Fetch account data (profile, addresses, payment methods) from backend.
                // Pass null as the user so fetchAccountData hits the API; passing the
                // login user object makes it skip the network call and leaves
                // addresses/paymentMethods empty.
                const userId = user._id || user.id;
                if (userId) {
                    fetchAccountData(userId, null);
                }
                
                // Check for pending tutoring reservation and link it to user
                const pendingReservation = localStorage.getItem('pendingTutoringReservation');
                if (pendingReservation) {
                    try {
                        const reservationData = JSON.parse(pendingReservation);
                        if (reservationData.reservationId) {
                            const linkApi = createBearerJsonClient(response.data.token, { baseURL: API_URL });

                            await linkApi.put(`/auth/tutoring-reservations/${reservationData.reservationId}/link-user`, {
                                userId: userId
                            });
                            
                            localStorage.removeItem('pendingTutoringReservation');
                            toast.success('Your reservation has been linked to your account!');
                        }
                    } catch (linkError) {
                        console.error('Error linking reservation to user:', linkError);
                        // Don't fail login if linking fails
                    }
                }
                
                // Check for pending commission request and link it to user
                const pendingCommission = localStorage.getItem('pendingCommissionRequest');
                if (pendingCommission) {
                    try {
                        const commissionData = JSON.parse(pendingCommission);
                        if (commissionData.commissionId) {
                            const linkApi = createBearerJsonClient(response.data.token, { baseURL: API_URL });

                            await linkApi.put(`/auth/commission-requests/${commissionData.commissionId}/link-user`, {
                                userId: userId
                            });
                            
                            localStorage.removeItem('pendingCommissionRequest');
                            toast.success('Your commission request has been linked to your account!');
                        }
                    } catch (linkError) {
                        console.error('Error linking commission request to user:', linkError);
                        // Don't fail login if linking fails
                    }
                }
                
                // Check for pending service request and link it to user
                const pendingServiceRequest = localStorage.getItem('pendingServiceRequest');
                if (pendingServiceRequest) {
                    try {
                        const serviceRequestData = JSON.parse(pendingServiceRequest);
                        if (serviceRequestData.requestId) {
                            const linkApi = createBearerJsonClient(response.data.token, { baseURL: API_URL });

                            await linkApi.put(`/auth/service-requests/${serviceRequestData.requestId}/link-user`, {
                                userId: userId
                            });
                            
                            localStorage.removeItem('pendingServiceRequest');
                            toast.success('Your service request has been linked to your account!');
                        }
                    } catch (linkError) {
                        console.error('Error linking service request to user:', linkError);
                        // Don't fail login if linking fails
                    }
                }
                
                // Check for pending service reservation and link it to user
                const pendingServiceReservation = localStorage.getItem('pendingServiceReservation');
                if (pendingServiceReservation) {
                    try {
                        const reservationData = JSON.parse(pendingServiceReservation);
                        if (reservationData.reservationId) {
                            const linkApi = createBearerJsonClient(response.data.token, { baseURL: API_URL });

                            await linkApi.put(`/auth/service-reservations/${reservationData.reservationId}/link-user`, {
                                userId: userId
                            });
                            
                            localStorage.removeItem('pendingServiceReservation');
                            toast.success('Your service reservation has been linked to your account!');
                        }
                    } catch (linkError) {
                        console.error('Error linking service reservation to user:', linkError);
                        // Don't fail login if linking fails
                    }
                }
                
                const returnTo = resolveAuthReturnPath(location);
                if (returnTo) {
                    navigate(returnTo, { replace: true });
                } else {
                    navigate('/account');
                }
            } else {
                trackClick('Login Submit - Error', { error: 'Invalid response' });
            }
        } catch (error) {
            console.error(error);
            trackClick('Login Submit - Error', { error: error?.message || 'Unknown error' });
            const code = error?.response?.data?.code;
            if (code === 'EMAIL_NOT_REGISTERED') {
                setAuthHint('no-account');
                toast.error(
                    t('auth.noAccountForEmail', "We don't have an account for this email yet — you can create one instead."),
                    { duration: 6000 },
                );
            } else if (code === 'INVALID_PASSWORD') {
                setAuthHint('wrong-password');
                toast.error(
                    t('auth.wrongPassword', "That password doesn't match this account. Please try again."),
                    { duration: 6000 },
                );
            } else if (code === 'EMAIL_NOT_VERIFIED') {
                setAuthHint('unverified');
                toast.error(
                    t('auth.confirmBeforeLogin', 'Please confirm your email before signing in.'),
                    { duration: 6000 },
                );
            } else {
                setAuthHint(null);
                toast.error(getAuthHttpErrorMessage(error, t));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="home">
            <div className="flex justify-center items-center" style={{ minHeight: 'calc(100vh - 100px)', paddingTop: '80px' }}>
                <section>
                    <h2>{t('common.login')}</h2>
                    <div className="auth-tabs">
                        <button type="button" className={`auth-tabs__btn ${tab === 'email' ? 'auth-tabs__btn--active' : ''}`} onClick={() => setTab('email')}>{t('auth.email', 'Email')}</button>
                        <button type="button" className={`auth-tabs__btn ${tab === 'phone' ? 'auth-tabs__btn--active' : ''}`} onClick={() => setTab('phone')}>{t('auth.phone', 'Phone')}</button>
                        {providers?.google?.enabled && (
                            <button type="button" className={`auth-tabs__btn ${tab === 'google' ? 'auth-tabs__btn--active' : ''}`} onClick={() => setTab('google')}>Google</button>
                        )}
                        {providers?.wechat?.enabled && (
                            <button type="button" className={`auth-tabs__btn ${tab === 'wechat' ? 'auth-tabs__btn--active' : ''}`} onClick={() => setTab('wechat')}>WeChat</button>
                        )}
                    </div>

                    <form className="login-form" onSubmit={handleSubmit}>
                        <input
                            type="email"
                            placeholder={t('auth.email')}
                            value={email || ''}
                            onChange={(e) => setEmail1(e.target.value)}
                            disabled={submitting}
                            required
                        />
                        <PasswordField
                            id="login-password"
                            placeholder={t('auth.password')}
                            value={password || ''}
                            onChange={(e) => setPassword1(e.target.value)}
                            disabled={submitting}
                            required
                            autoComplete="current-password"
                        />
                        <button type="submit" disabled={submitting}>
                            {submitting ? t('common.loading', 'Loading...') : t('common.login')}
                        </button>
                        <button type="button" disabled={submitting} onClick={handleRegister}>
                            {t('common.newUser')}
                        </button>
                        {authHint === 'unverified' && (
                            <div style={{ marginTop: '0.75rem', fontSize: '0.95rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                <span>{t('auth.confirmBeforeLoginHint', 'Check your inbox for a confirmation link, or resend it below.')}</span>
                                <button
                                    type="button"
                                    onClick={handleResendConfirmation}
                                    disabled={resending}
                                    style={{ alignSelf: 'flex-start', cursor: 'pointer', border: '1px solid var(--brand, #3b82f6)', background: 'transparent', color: 'var(--brand, #3b82f6)', borderRadius: '6px', padding: '0.4rem 0.9rem', fontWeight: 600 }}
                                >
                                    {resending ? t('common.loading', 'Loading...') : t('auth.resendConfirmation', 'Resend confirmation email')}
                                </button>
                            </div>
                        )}
                        {authHint === 'no-account' && (
                            <div style={{ marginTop: '0.75rem', fontSize: '0.95rem' }}>
                                <span>{t('auth.noAccountHint', 'No account found for this email yet.')}</span>
                            </div>
                        )}
                        {authHint === 'wrong-password' && (
                            <div style={{ marginTop: '0.75rem', fontSize: '0.95rem' }}>
                                <span>{t('auth.wrongPasswordHint', "That password doesn't match this account. Please try again.")}</span>
                            </div>
                        )}
                    </form>

                    {tab === 'phone' && (
                        <PhoneAuthPanel mode="login" next={nextPath} />
                    )}

                    {tab === 'google' && (
                        <div className="social-login">
                            <button type="button" className="social-login__btn social-login__btn--google" onClick={() => startSocialLogin('google', { next: nextPath })}>
                                <span>Continue with Google</span>
                            </button>
                        </div>
                    )}

                    {tab === 'wechat' && (
                        <div className="social-login">
                            {wechatQr ? (
                                <div className="social-login__qr" role="dialog" aria-label="Scan with WeChat to sign in">
                                    <iframe title="WeChat sign-in" src={wechatQr} width="280" height="360" frameBorder="0" scrolling="no" />
                                </div>
                            ) : (
                                <button type="button" className="social-login__btn social-login__btn--wechat" onClick={handleWechat}>
                                    <span>Continue with WeChat</span>
                                </button>
                            )}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}

export default Login; 


